
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { LineChart, ActivitySquare, TrendingUp, Users, Volume2, MessageCircle } from 'lucide-react';
import { ArtistHealthCheck } from '@/types';
import ArtistHealthMetric from '../ArtistHealthMetric';
import ArtistRecommendationCard from '../ArtistRecommendationCard';
import { getStatusColor, formatGrowth } from '../utils/healthcheckUtils';

interface CareerHealthTabContentProps {
  healthCheck: ArtistHealthCheck;
  onRequestUpdate: () => void;
}

const CareerHealthTabContent: React.FC<CareerHealthTabContentProps> = ({ 
  healthCheck,
  onRequestUpdate 
}) => {
  const { scores, recommendations, growth, nextMilestone } = healthCheck;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="col-span-1 md:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl mb-1">Artist Health Check</CardTitle>
              <CardDescription>
                Last updated: {healthCheck.lastUpdated.toLocaleDateString()}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onRequestUpdate}>
              Refresh Analysis
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div className="col-span-1 sm:col-span-2 md:col-span-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <h3 className="text-lg font-medium">Current Status</h3>
                  <div className="flex items-center mt-1">
                    <Badge 
                      variant="soundcharts-filled" 
                      className={`px-3 py-1 text-white ${getStatusColor(healthCheck.overallStatus)}`}
                    >
                      {healthCheck.overallStatus}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">Overall Score:</span>
                    <span className="font-medium">{scores.overall}/100</span>
                  </div>
                  <Progress value={scores.overall} className="w-36 h-2" />
                </div>
              </div>
            </div>
            
            <ArtistHealthMetric 
              title="Streaming" 
              value={scores.streaming} 
              icon={<Volume2 className="h-4 w-4" />}
              change={growth.streamingGrowth}
              changeLabel={`${formatGrowth(growth.streamingGrowth)} this ${growth.period}`}
            />
            
            <ArtistHealthMetric 
              title="Social Engagement" 
              value={scores.socialEngagement} 
              icon={<MessageCircle className="h-4 w-4" />}
              change={growth.engagementGrowth}
              changeLabel={`${formatGrowth(growth.engagementGrowth)} this ${growth.period}`}
            />
            
            <ArtistHealthMetric 
              title="Fan Growth" 
              value={scores.fanGrowth} 
              icon={<Users className="h-4 w-4" />}
              change={growth.followerGrowth}
              changeLabel={`${formatGrowth(growth.followerGrowth)} this ${growth.period}`}
            />
            
            <ArtistHealthMetric 
              title="Collaborations" 
              value={scores.collaborations} 
              icon={<ActivitySquare className="h-4 w-4" />}
            />
            
            <ArtistHealthMetric 
              title="Marketing" 
              value={scores.marketing} 
              icon={<TrendingUp className="h-4 w-4" />}
            />
            
            <ArtistHealthMetric 
              title="Content" 
              value={scores.content} 
              icon={<LineChart className="h-4 w-4" />}
            />
          </div>
          
          <div className="p-4 bg-muted/30 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Next Milestone</h3>
            <div className="space-y-2">
              <p>{nextMilestone.description}</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span>{nextMilestone.current} / {nextMilestone.target}</span>
                </div>
                <Progress 
                  value={(nextMilestone.current / nextMilestone.target) * 100} 
                  className="h-2" 
                />
                <p className="text-xs text-muted-foreground">
                  Estimated time to achievement: {nextMilestone.eta}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Action Plan</CardTitle>
          <CardDescription>Personalized recommendations</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {recommendations.map((rec, index) => (
            <ArtistRecommendationCard key={index} recommendation={rec} priority={index === 0} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default CareerHealthTabContent;
